// @flow
import Entity from '../entity';

import type { Player$ControllerOptions } from '../types/Options';

const MS_IN_SEC = 1000;

export default class BasePlayer extends Entity {
   options: Player$ControllerOptions
   state;
   constructor(options: Player$ControllerOptions, state) {
      super(options.bus, options.logger);
      this.state = state;
      this.logger.debug('player:created');
      this.options = options;
      setTimeout( () => this.getElement().setAttribute('tabindex', '-1') );
   }

   focus() {
      let el = this.getElement();
      // fix for ff
      if ( this.options.isAudio ) {
         // $FlowFixMe
         el = el.parentNode.parentNode;
      }

      if ( el && el instanceof HTMLElement ) {
         el.focus();
      }
   }

   getTimeCode(): number {
      return this.options.timecode;
   }

   getCurrentTime() {
      return this.getElement().currentTime + this.getTimeCode();
   }

   setCurrentTime(time: number, silent: ?boolean = true) {
      if (!this.getElement()) return;

      time -= this.getTimeCode();

      const { clip } = this.options;
      if ( clip ) {
         let [ start, end ]  = clip;
         start = start || 0;
         end = end || this.getElement().duration;
         time = Math.max(time, start);
         time = Math.min(time, end);
      }

      this.getElement().currentTime = time;
      if ( !silent ) {
         this.focus();
      }
   }

   nextFrame() {
      const frameDuration = this._getFrameDuration();
      let time = this.getElement().currentTime;
      let frameNumber = Math.floor(time * MS_IN_SEC / frameDuration);
      time = Math.ceil(++frameNumber * frameDuration) / MS_IN_SEC;
      this.setCurrentTime(time + this.getTimeCode());
   }

   prevFrame() {
      const frameDuration = this._getFrameDuration();
      let time = this.getElement().currentTime;
      let frameNumber = Math.floor(time * MS_IN_SEC / frameDuration);
      time = Math.ceil(--frameNumber * frameDuration) / MS_IN_SEC;
      this.setCurrentTime(time + this.getTimeCode());
   }

   isPaused() {
      if (!this.getElement()) return false;
      return this.getElement().paused;
   }

   isEnded() {
      if (!this.getElement()) return false;

      const { clip } = this.options;
      if ( clip ) {
         const duration = this.getElement().duration;
         let [ start, end ]  = clip; // eslint-disable-line no-unused-vars
         end = end || duration;
         end += this.getTimeCode();
         return this.getCurrentTime() >= end;
      }
      return this.getElement().ended;
   }

   play(silent: ?boolean = true) {
      this.bus.emit('handle-play-clicked');
      if (!silent) {
         this.focus();
      }

      const { clip } = this.options;
      if ( this.isEnded() && clip ) {
         const [ start ] = clip;
         this.setCurrentTime(start || 0);
      }
      return this.getElement().play();
   }

   pause(silent: ?boolean = true) {
      if (!silent) {
         this.focus();
      }
      return this.getElement().pause();
   }

   getDuration() {
      if (!this.getElement()) return '';

      let duration = this.getElement().duration;
      const { clip } = this.options;
      if ( clip ) {
         let [ start, end ]  = clip;
         start = start || 0;
         end = end || duration;
         duration = end - start;
      }
      return duration + this.getTimeCode();
   }

   isMuted() {
      return this.getElement().muted;
   }

   setMuted(muted: boolean, silent: ?boolean = true) {
      this.getElement().muted = muted;
      if (!silent) {
         this.focus();
      }
   }

   getVolume() {
      return this.getElement().volume;
   }

   setVolume(volume: number, silent: ?boolean = true) {
      this.getElement().volume = volume;
      if (!silent) {
         this.focus();
      }
   }

   destroy() {
      this.logger.debug('player:destroy');
      super.destroy();
   }

   getLevels() {
      throw new Error('You must override it');
   }

   getLevel() {
      throw new Error('You must override it');
   }

   setLevel(/* level */) {
      throw new Error('You must override it');
   }

   isMultiAudioTracks() {
      return this.getAudioTracks().length > 1;
   }

   getAudioTracks() {
      return [];
   }

   getAudioTrack() {
      return 0;
   }

   setAudioTrack(idx) {
      // noop;
   }

   getTextTracks() {
      // noop;
   }

   load(/* sources */) {
      throw new Error('You must override it');
   }

   isMultiQuality(): boolean {
      // eslint-disable-next-line no-magic-numbers
      return this.getLevels().length > 1;
   }

   getBufferedPercent(): number {
      return 0;
   }

   getElement(): HTMLVideoElement | HTMLAudioElement {
      throw new Error('You must override it');
   }

   _getFrameDuration(): number {
      return MS_IN_SEC / this.options.frameRate;
   }
}
